import axios from "axios";
import { useEffect, useMemo, useState } from "react";

import { errorNotify, successNotify } from "../../../Services/Notification.js";
import TemplatePMSHeader from "../../../components/backoffice/TemplatePMS/TemplatePMSHeader.jsx";
import TemplatePMSForm from "../../../components/backoffice/TemplatePMS/TemplatePMSForm.jsx";
import TemplatePMSMappingForm from "../../../components/backoffice/TemplatePMS/TemplatePMSMappingForm.jsx";
import {
  getList,
  getMappingScheme,
  getMappingTemplate,
  getTemplateId,
} from "../../../components/backoffice/TemplatePMS/templatePmsUtils.js";
import Breadcrumbs from "../../../components/ui/Breadcrumbs.jsx";
import TemplatePMSConfig from "./TemplatePMSConfig.jsx";

const apiUrl = `${process.env.REACT_APP_API_URL}api/pms-template`;
const pmsApiUrl = `${process.env.REACT_APP_API_URL}api/pms/list_all/`;

const getHeaders = () => ({
  Authorization: `Bearer ${
    sessionStorage.getItem("subscriberToken") || sessionStorage.getItem("token")
  }`,
});

const TemplatePMS = () => {
  const [templates, setTemplates] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popup, setPopup] = useState(null);
  const [filters, setFilters] = useState({ template: "", scheme: "" });
  const [syncing, setSyncing] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      const [templateResponse, mappingResponse, pmsResponse] = await Promise.all([
        axios.get(`${apiUrl}/list_templates/`, { headers }),
        axios.get(`${apiUrl}/list_mappings/`, { headers }),
        axios.get(pmsApiUrl, { headers }),
      ]);

      setTemplates(getList(templateResponse));
      setMappings(getList(mappingResponse));
      setSchemes(getList(pmsResponse).flatMap((pms) => {
        const list = pms?.schemes || pms?.scheme || pms?.scheme_list || pms?.children || [];
        return list.map((scheme) => ({
          ...scheme,
          label: scheme?.name || scheme?.scheme_name || scheme?.code || "-",
        }));
      }));
    } catch (error) {
      errorNotify(error?.response?.data?.message || "Unable to fetch PMS templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveTemplate = async (payload, isEdit) => {
    try {
      const response = await axios.post(
        `${apiUrl}/${isEdit ? "update_template" : "create_template"}/`,
        payload,
        { headers: getHeaders() },
      );
      if (response.data?.error) throw new Error(response.data?.message);
      successNotify(response.data?.message || `Template ${isEdit ? "updated" : "created"} successfully.`);
      await loadData();
      return true;
    } catch (error) {
      errorNotify(error?.response?.data?.message || error.message || "Unable to save template.");
      return false;
    }
  };

  const saveMapping = async (payload, isEdit) => {
    try {
      const isFormData = payload instanceof FormData;
      const response = await axios.post(
        `${apiUrl}/${isEdit ? "update_mapping" : "create_mapping"}/`,
        payload,
        { headers: isFormData ? getHeaders() : { ...getHeaders(), "Content-Type": "application/json" } },
      );
      if (response.data?.error) throw new Error(response.data?.message);
      successNotify(response.data?.message || `Mapping ${isEdit ? "updated" : "created"} successfully.`);
      await loadData();
      return true;
    } catch (error) {
      errorNotify(error?.response?.data?.message || error.message || "Unable to save mapping.");
      return false;
    }
  };

  const handleSyncData = async (refreshUptoId, type) => {
    setSyncing(type);
    try {
      const response = await axios.post(
        `${apiUrl}/sync_template_to_instance/`,
        {
          refresh_upto_id: refreshUptoId,
          sync_data_to: type,
        },
        { headers: { ...getHeaders(), "Content-Type": "application/json" } },
      );
      if (response.data?.error) throw new Error(response.data?.message);
      successNotify(response.data?.message || `Templates synced to ${type}.`);
      await loadData();
      return true;
    } catch (error) {
      errorNotify(error?.response?.data?.message || error.message || "Unable to sync templates.");
      return false;
    } finally {
      setSyncing(null);
    }
  };

  const schemeOptions = useMemo(() => {
    const options = new Map(schemes.map((scheme) => [String(scheme.id), scheme]));
    mappings.forEach((mapping) => {
      if (mapping?.pms_scheme && typeof mapping.pms_scheme === "object") {
        const scheme = getMappingScheme(mapping, schemes);
        if (scheme?.id !== undefined) options.set(String(scheme.id), scheme);
      }
    });
    return Array.from(options.values());
  }, [mappings, schemes]);

  const rows = useMemo(() => mappings.map((mapping) => ({
    mapping,
    template: getMappingTemplate(mapping, templates) || {},
    scheme: getMappingScheme(mapping, schemes) || {},
  })).filter(({ template, scheme }) =>
    (!filters.template || String(getTemplateId(template)) === filters.template) &&
    (!filters.scheme || String(scheme.id) === filters.scheme),
  ), [mappings, templates, schemes, filters]);

  return (
    <div className="app-container min-h-[65vh] pb-[60px] pt-[100px]">
      <Breadcrumbs items={["Home", "Back Office", "PMS Template Mapping"]} />
      <TemplatePMSHeader
        templates={templates}
        schemes={schemeOptions}
        filters={filters}
        setFilters={setFilters}
      />
      <TemplatePMSConfig
        rows={rows}
        templates={templates}
        schemes={schemes}
        loading={loading}
        syncing={syncing}
        handleSyncData={handleSyncData}
        setPopup={setPopup}
      />

      {popup?.type === "template" && (
        <TemplatePMSForm
          data={popup.data}
          templates={templates}
          onClose={() => setPopup(null)}
          onSubmit={saveTemplate}
        />
      )}
      {popup?.type === "mapping" && (
        <TemplatePMSMappingForm
          data={popup.data}
          templates={templates}
          schemes={schemes}
          onClose={() => setPopup(null)}
          onSubmit={saveMapping}
        />
      )}
    </div>
  );
};

export default TemplatePMS;
